import React, { useMemo, useState } from 'react';
import { AccessTimeRounded, Star } from '@mui/icons-material';
import Button from '@/components/input/Button';
import { ContentType } from '@/services/contents/types';
import { contentsService } from '@/services/contents';
import useLearningContents from '@/context/contents/hook';
import { formatContentDuration } from '@/utils/formatContentDuration';
import { getContentDetail } from '../common';
import styles from './styles';
import ContentCoverPlaceholder from './placeholder';

interface Props {
  contentId: string;
  title?: string;
  duration?: number;
  type?: ContentType;
  overviewData?: string;
  userRouteProgressId?: string;
  routeId?: string;
  stageId?: string;
  loading?: boolean;
}

const ContentCover: React.FC<Props> = ({
  contentId,
  userRouteProgressId,
  title,
  duration,
  type,
  routeId,
  stageId,
  overviewData,
  loading,
}) => {
  const [startingContent, setStartingContent] = useState(false);

  const contentIndicators = getContentDetail(type);
  const ContentIcon = contentIndicators.Icon;
  const { updateLearningContent, updateStageContent } = useLearningContents();

  const overview = useMemo(() => JSON.parse(overviewData ?? '{}'), [overviewData]);

  const handleClick = () => {
    if (!contentId || !routeId || !userRouteProgressId || startingContent) return;
    setStartingContent(true);

    contentsService
      .createContentInteraction({
        contentId,
        userRouteProgressId,
      })
      .then((response) => {
        const { lastVisitedAt, visitCount, id } = response;
        if (!stageId) {
          updateLearningContent(routeId, contentId, {
            lastVisitedAt,
            visitCount,
            contentInteractionId: id,
          });
        } else {
          updateStageContent(routeId, stageId, contentId, {
            lastVisitedAt,
            visitCount,
            contentInteractionId: id,
          });
        }
      })
      .finally(() => {
        setStartingContent(false);
      });
  };

  if (loading) return <ContentCoverPlaceholder />;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>{title}</h1>
      <div style={styles.indicatorContainer}>
        <p style={styles.indicator}>Duración</p>
        <p style={styles.indicatorContent}>
          <AccessTimeRounded /> <span>{formatContentDuration(duration ?? 0)}</span>
        </p>
      </div>

      <div style={styles.indicatorContainer}>
        <p style={styles.indicator}>Tipo de contenido</p>
        <p style={styles.indicatorContent}>
          <ContentIcon /> <span>{contentIndicators.title}</span>
        </p>
      </div>

      {overview?.importance && (
        <div style={styles.indicatorContainer}>
          <p style={styles.indicator}>¿Por qué es importante ? </p>
          <p style={styles.indicatorContent}>{overview?.importance}</p>
        </div>
      )}
      {overview?.benefits?.length > 0 && (
        <div style={styles.indicatorContainer}>
          <p style={styles.indicator}>Beneficios</p>
          <ul style={styles.benefitsContainer}>
            {overview?.benefits.map((benefit: string, index: number) => (
              <li key={index} style={styles.benefitItem}>
                <span style={styles.benefitIcon}>
                  <Star />
                </span>
                <p>{benefit}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Button
        variant="main"
        sx={{ marginTop: 'auto' }}
        disabled={startingContent}
        onClick={handleClick}
        loading={startingContent}
      >
        {startingContent ? 'Cargando...' : 'Empezar'}
      </Button>
    </div>
  );
};

export default ContentCover;
